import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { Upload, X, Loader2, FileText, Image, CheckCircle } from "lucide-react";
import { toast } from "sonner";

export function DocumentDropzone({ type, onResult, onClose, onFileStored }) {
  const [processing, setProcessing] = useState(false);
  const [preview, setPreview] = useState(null);
  const [fileName, setFileName] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const processFile = useCallback(async (file) => {
    const isImage = file.type.startsWith('image/');
    const isPDF = file.type === 'application/pdf';
    const isValidExt = file.name.match(/\.(pdf|jpg|jpeg|png|webp|heic|heif)$/i);

    if (!isImage && !isPDF && !isValidExt) {
      toast.error("Formato non supportato. Usa PDF, JPG, PNG o WebP");
      return;
    }

    setFileName(file.name);
    setProcessing(true);
    
    // Store original file for later upload
    if (onFileStored) onFileStored(file);

    try {
      if (isPDF || file.name.match(/\.pdf$/i)) {
        setPreview("pdf");
        // Use parse-pdf-complete (same as Import Data page)
        const formData = new FormData();
        formData.append("file", file);

        const response = await axios.post("/api/ai/parse-pdf-complete", formData, {
          headers: { "Content-Type": "multipart/form-data" }
        });

        const data = response.data;
        let parsed = {};
        
        // Show validation warnings
        const warnings = data.warnings || [];
        if (warnings.length > 0) {
          warnings.forEach(w => {
            toast.warning(w.message, { duration: 8000 });
          });
        }

        if (type === "client") {
          parsed = data.client_data || {};
          // Also check detected_documents for ID card data
          if (data.detected_documents) {
            for (const doc of data.detected_documents) {
              if (doc.type === "identity_card" || doc.type === "id_card") {
                parsed = { ...parsed, ...doc.data };
              }
            }
          }
        } else if (type === "policy") {
          parsed = data.policy_data || {};
          // Pass client info for matching in the policy form
          if (data.client_data?.name) {
            parsed.client_name = data.client_data.name;
          }
          if (data.client_data?.fiscal_code) {
            parsed.fiscal_code = data.client_data.fiscal_code;
          }
          if (data.existing_client?.client_id) {
            parsed.client_id = data.existing_client.client_id;
          }
          if (data.client_exists) {
            toast.info(`Cliente trovato nel database: ${data.existing_client?.name || data.client_data?.name}`);
          }
        }

        if (parsed && Object.keys(parsed).length > 0) {
          toast.success(`Documento analizzato: ${data.pages_processed || 1} pagine`);
          onResult(parsed);
        } else {
          toast.error("Non sono riuscito ad estrarre dati dal documento");
        }
      } else {
        // Image - use parse-image
        const base64 = await new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.onload = () => resolve(reader.result);
          reader.onerror = reject;
          reader.readAsDataURL(file);
        });

        setPreview(base64);

        const response = await axios.post('/api/ai/parse-image', {
          image: base64,
          type: type
        });

        if (response.data.parsed_data && Object.keys(response.data.parsed_data).length > 0) {
          // Show validation warnings
          const warnings = response.data.warnings || [];
          if (warnings.length > 0) {
            warnings.forEach(w => {
              toast.warning(w.message, { duration: 8000 });
            });
          }
          toast.success("Dati estratti con successo dal documento");
          onResult(response.data.parsed_data);
        } else {
          toast.error("Non sono riuscito ad estrarre dati dall'immagine");
        }
      }
    } catch (error) {
      console.error("Document parse error:", error);
      toast.error(error.response?.data?.detail || "Errore nell'analisi del documento");
    } finally {
      setProcessing(false);
    }
  }, [type, onResult]);

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      processFile(files[0]);
    }
  };

  const reset = () => {
    setPreview(null);
    setFileName(null);
    setProcessing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="relative rounded-md overflow-hidden">
      {/* Close button */}
      <Button
        type="button"
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 z-10 h-6 w-6"
        onClick={onClose}
       aria-label="Rimuovi il file">
        <X className="h-3 w-3" />
      </Button>

      {processing ? (
        <div className="flex flex-col items-center justify-center py-8 bg-primary/5 border-2 border-dashed border-primary/30 rounded-md">
          <Loader2 className="h-8 w-8 animate-spin text-primary mb-3" />
          <p className="text-sm font-medium">Analisi documento in corso...</p>
          {fileName && (
            <p className="text-xs text-muted-foreground mt-1">{fileName}</p>
          )}
        </div>
      ) : preview ? (
        <div className="flex flex-col items-center gap-3 py-6 bg-green-50 dark:bg-green-950/20 border-2 border-dashed border-green-400 rounded-md">
          <CheckCircle className="h-8 w-8 text-green-500" />
          <p className="text-sm font-medium text-green-700 dark:text-green-400">
            Documento elaborato
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={reset}
          >
            Carica un altro
          </Button>
        </div>
      ) : (
        <div
          className={`
            flex flex-col items-center justify-center py-8 cursor-pointer rounded-md
            border-2 border-dashed transition-all duration-200
            ${isDragging
              ? 'border-primary bg-primary/10 scale-[1.01]'
              : 'border-border hover:border-primary/50 hover:bg-accent/30'
            }
          `}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          data-testid={`doc-dropzone-${type}`}
        >
          <div className="flex gap-2 mb-3">
            <Image className="h-5 w-5 text-muted-foreground" />
            <FileText className="h-5 w-5 text-red-400" />
          </div>
          <p className="text-sm font-medium">
            {isDragging ? 'Rilascia il file qui' : 'Trascina un documento qui'}
          </p>
          <p className="text-xs text-muted-foreground mt-1">
            oppure clicca per selezionare - PDF, JPG, PNG
          </p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {type === 'client' ? 'Carta d\'identit\u00e0, tessera sanitaria, documento' : 'Polizza, quietanza, contratto'}
          </p>
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,application/pdf"
        onChange={handleFileSelect}
        className="hidden"
        data-testid={`doc-input-${type}`}
      />
    </div>
  );
}

export default DocumentDropzone;
